import { action, asyncAction } from '@play-co/replicant';
import createActions from 'src/replicant/config/actions/createActions';
import {
  FriendshipRequest,
  type FriendShareMethod,
  PlayerID,
  PlayerProfile,
} from './friends.ruleset';
import {
  canAddFriends,
  getBuruFriendsCount,
  getFriendshipRequestMethod,
  isBuruFriend,
  isFriendshipRequestedSent,
} from './friends.getters';
import { addFriend, removeFriend } from './friends.modifiers';
import { getCurrentEvolution } from '../pet/pet.getters';
import { getChatbotFlexMessage } from '../chatbot/flex/utils';
import { generateReplicantChatbotPayload } from '../chatbot/messageTemplates';
import { Feature, sendAnalyticsEvents } from '../analytics';
import { getLocale } from '../getters/user';
import { deliverChatbotMessage } from '../chatbot/chatbot.util';
import { setMatch2Timestamp } from '../match2/match2.modifiers';
import { getPlayerProfileAvatarFrame } from '../profile/profile.getters';
import { deliverFriendRequestAcceptedMessage } from './friends.chatbot';

export default createActions({
  /**
   * Create friendship request on current and on target user state
   */
  createFriendshipRequest: action(
    (
      state,
      args: {
        target: PlayerProfile;
        sourceFeature?: string;
        originFeature?: string;
        originSubFeature?: string;
        method?: string;
      },
      api,
    ) => {
      const { target, sourceFeature, originFeature, originSubFeature, method } =
        args;
      const now = api.date.now();

      if (!canAddFriends(state)) {
        throw new Error('Friends limit reached');
      }

      if (state.id === target.id) {
        throw new Error('You can not send friend request to yourself');
      }

      if (!state.publicProfile) {
        throw new Error('No public profile found');
      }

      if (isFriendshipRequestedSent(state, target.id)) {
        throw new Error('Friend request already sent');
      }

      if (isBuruFriend(state, target.id)) {
        throw new Error('Already friends');
      }

      if (state.buruFriends[target.id]?.unfriendedAt !== undefined) {
        throw new Error('You have been unfriended from this user');
      }

      // Create owner profile
      const owner: PlayerProfile = {
        id: state.id,
        // Don't use private profile here! Because it sends outside
        name: state.publicProfile.name ?? 'Unknown',
        profilePicture: state.publicProfile.photo ?? '',
        frame: getPlayerProfileAvatarFrame(state),
        species: getCurrentEvolution(state)?.species ?? 'ball',
      };

      // Create generic friendship request
      const request: FriendshipRequest = {
        target,
        owner,
        status: 'pending',
        createdAt: now,
        updatedAt: now,
        sourceFeature,
        originFeature,
        originSubFeature,
        method,
      };

      // Create request on owner user state
      state.friendshipRequests[target.id] = request;

      // Create request on target user state too
      api.postMessage.createFriendshipRequest(target.id, request);

      const message = getChatbotFlexMessage({
        template: 'friendshipRequestMessage',
        payload: generateReplicantChatbotPayload({
          feature: Feature.FRIENDS._,
          $subFeature: Feature.FRIENDS.REQUEST,
          originFeature: request.originFeature,
          originSubFeature: request.originSubFeature,
          friendRequestSenderID: state.id,
          friendRequestReceiverID: target.id,
        }),
        data: {
          sender: {
            photo: owner.profilePicture,
            name: owner.name,
            species: owner.species,
          },
          locale: getLocale(state),
        },
      });

      // Send chatbot OA message
      deliverChatbotMessage(
        state,
        api,
        { receiverId: target.id, message, channel: 'oa' }, // Force OA channel
      );
    },
  ),

  /**
   * Accept friendship request
   */
  acceptFriendshipRequest: action((state, args: FriendshipRequest, api) => {
    const request = state.friendshipRequests[args.owner.id];

    if (!request) {
      throw new Error(
        `No friendship request found from ${args.owner.id} found`,
      );
    }

    if (request.status !== 'pending') {
      throw new Error(
        `Friendship request from ${args.owner.id} already ${request.status}`,
      );
    }

    // Add to social friends list
    addFriend(state, args.owner.id, api.date.now());

    // Respond to owner
    const isDummy =
      process.env.IS_DEVELOPMENT && args.owner.id.startsWith('Udummy');

    if (!isDummy) {
      api.postMessage.acceptFriendshipRequest(request.owner.id, {});

      // Skip OA if the acceptor has no public profile — matches the
      // `addFriendFromInvite` guard.
      if (state.publicProfile) {
        deliverFriendRequestAcceptedMessage(
          {
            receiverId: request.owner.id,
            acceptorState: state,
            now: api.date.now(),
            originFeature: request.originFeature,
            originSubFeature: request.originSubFeature,
          },
          api,
        );
      }
    }

    // The friendship now lives in `buruFriends`; the request has no reader on this side past
    // this point, and at two full profiles it is the largest thing a request carries. The sender
    // keeps their own copy until their `FriendAdd` is emitted.
    delete state.friendshipRequests[args.owner.id];
  }),

  /**
   * Reject friendship request
   */
  rejectFriendshipRequest: action((state, args: FriendshipRequest, api) => {
    const request = state.friendshipRequests[args.owner.id];

    if (!request) {
      throw new Error(
        `No friendship request found from ${args.owner.id} found`,
      );
    }

    if (request.status !== 'pending') {
      throw new Error(
        `Friendship request from ${args.owner.id} already ${request.status}`,
      );
    }

    // Change state of request
    state.friendshipRequests[args.owner.id].status = 'rejected';

    // Respond to request owner
    const isDummy =
      process.env.IS_DEVELOPMENT && args.owner.id.startsWith('Udummy');

    if (!isDummy) {
      api.postMessage.rejectFriendshipRequest(request.owner.id, {});
    }
  }),

  friendsUnderMaintenancePopupSeen: action((state, args: void, api) => {
    setMatch2Timestamp(
      state,
      'friendsUnderMaintenancePopupSeen',
      api.date.now(),
    );
  }),

  /**
   * Unfriend a friend and block the ability for him/her to send requests to the user
   */
  unfriend: action((state, friendId: PlayerID, api) => {
    if (!isBuruFriend(state, friendId)) {
      return;
    }

    // Remove friend from the state
    removeFriend(state, friendId);

    const isDummy = process.env.IS_DEVELOPMENT && friendId.startsWith('Udummy');

    if (!isDummy) {
      /**
       * Notify the friend to also mark user as unfriended,
       * which blocks him/her from the ability to send new requests to the user
       */
      api.postMessage.unfriend(friendId, {});
    }
  }),

  /**
   * Automatically add friend from invite (Add Friends V2 flow).
   * When recipient lands via invite link, automatically establish friendship on both sides.
   * Return true if friend was added, false if not (eg. limit reached, already friends, etc).
   */
  addFriendFromInvite: asyncAction(
    async (
      state,
      args: {
        inviterId: string;
        method?: FriendShareMethod;
        originFeature?: string;
        originSubFeature?: string;
      },
      api,
    ) => {
      const now = api.date.now();
      const recipientState = state;
      let inviterPostAddFriendCount = 0;
      const hasSenderAttribution =
        args.method !== undefined &&
        args.originFeature !== undefined &&
        args.originSubFeature !== undefined;

      // Check if inviter is dummy
      const isDummy =
        process.env.IS_DEVELOPMENT && args.inviterId.startsWith('Udummy');

      // Check if recipient can add friends
      if (!canAddFriends(recipientState)) {
        return false;
      }

      // Check if inviter is self
      if (args.inviterId === recipientState.id) {
        return false;
      }

      // Check if already friends
      if (recipientState.buruFriends[args.inviterId]) {
        return false;
      }

      if (!isDummy) {
        // Fetch inviter's state to verify they exist and can add friends
        const inviterStatesMap = await api.fetchStates([args.inviterId]);
        const inviterStateEntry = inviterStatesMap?.[args.inviterId];
        const inviterState = inviterStateEntry?.state;

        // If inviter is missing, don't proceed (treat as invalid invite)
        if (!inviterState) {
          return false;
        }

        // Check if inviter is already friends with recipient
        if (inviterState.buruFriends[recipientState.id]) {
          return false;
        }

        // Check if inviter can add friends
        if (!canAddFriends(inviterState)) {
          return false;
        }

        inviterPostAddFriendCount = getBuruFriendsCount(inviterState) + 1;
      }

      // Capture origin from the stored request before it is deleted so it
      // can be propagated onto the return-accept OA payload below.
      const existingRequest = recipientState.friendshipRequests[args.inviterId];
      const requestOriginFeature = existingRequest?.originFeature;
      const requestOriginSubFeature = existingRequest?.originSubFeature;

      // Clean up any existing friendship request from inviter to recipient
      if (existingRequest) {
        delete recipientState.friendshipRequests[args.inviterId];
      }

      // Add inviter as friend on recipient's side
      addFriend(recipientState, args.inviterId, now);

      // For new users, set invitedBy so OA message can be sent when they create their profile
      if (recipientState.loginCount === 1) {
        recipientState.social.referral.invitedBy = args.inviterId;
      }

      if (!isDummy) {
        // Notify inviter to add recipient as friend on their side
        api.postMessage.addFriendFromInvite(args.inviterId, {
          friendId: recipientState.id,
        });

        if (hasSenderAttribution) {
          // The inviter can be offline when the recipient accepts the shared
          // link, so emit their FriendAdd as an inactive cross-user event.
          sendAnalyticsEvents(api, [
            {
              userId: args.inviterId,
              eventType: 'FriendAdd',
              eventProperties: {
                friendCount: inviterPostAddFriendCount,
                method: getFriendshipRequestMethod(args.method),
                originFeature: args.originFeature,
                originSubFeature: args.originSubFeature,
                friendRequestReceiverID: recipientState.id,
                friendRequestSenderID: args.inviterId,
                isReceiver: false,
              },
            },
          ]);
        }

        // Send OA message to inviter when an existing user accepts invite confirmation.
        // - Skip new users (handled elsewhere)
        // - Skip if recipient lacks public profile
        const shouldNotifyInviter =
          recipientState.loginCount !== 1 && !!recipientState.publicProfile;

        if (shouldNotifyInviter) {
          deliverFriendRequestAcceptedMessage(
            {
              receiverId: args.inviterId,
              acceptorState: recipientState,
              now,
              originFeature: requestOriginFeature,
              originSubFeature: requestOriginSubFeature,
            },
            api,
          );
        }
      }

      return true;
    },
  ),

  /**
   * Restore a friendship when a share link reveals that only one user still
   * has the other in their active friends list.
   *
   * @param friendId Player whose reciprocal friendship should be checked.
   * @returns Whether the friendship was already reciprocal or was restored.
   */
  reconcileFriends: asyncAction(async (state, friendId: PlayerID, api) => {
    // Friends cannot be reconciled with the current user.
    if (friendId === state.id) {
      return false;
    }

    // Dummy friends do not have a persistent remote state to reconcile.
    const isDummy = process.env.IS_DEVELOPMENT && friendId.startsWith('Udummy');
    if (isDummy) {
      return false;
    }

    // Fetch the friend's state so both friendship sides can be compared.
    const friendStatesMap = await api.fetchStates([friendId]);
    const friendStateEntry = friendStatesMap?.[friendId];
    const friendState = friendStateEntry?.state;

    // Missing players cannot participate in reconciliation.
    if (!friendState) {
      return false;
    }

    // Already reciprocal friendships need no mutation, while completely new
    // friendships must continue through the normal invite action.
    const currentUserIsFriend = isBuruFriend(state, friendId);
    const otherUserIsFriend = isBuruFriend(friendState, state.id);
    if (currentUserIsFriend === otherUserIsFriend) {
      return currentUserIsFriend;
    }

    // Do not overwrite an explicit unfriended marker on the current user.
    const currentUserFriendState = state.buruFriends[friendId];
    if (!currentUserIsFriend && currentUserFriendState) {
      return false;
    }

    // Do not overwrite an explicit unfriended marker on the other user.
    const otherUserFriendState = friendState.buruFriends[state.id];
    if (!otherUserIsFriend && otherUserFriendState) {
      return false;
    }

    // Restore the current user's missing side when there is capacity.
    if (!currentUserIsFriend) {
      if (!canAddFriends(state)) {
        return false;
      }

      // Remove a stale request before restoring the friendship.
      delete state.friendshipRequests[friendId];

      // Add only the missing current-user side.
      addFriend(state, friendId, api.date.now());
      return true;
    }

    // The other user must have capacity for the missing reciprocal side.
    if (!canAddFriends(friendState)) {
      return false;
    }

    // Remove a stale local request before notifying the other user.
    delete state.friendshipRequests[friendId];

    // Add only the missing remote side through the existing message handler.
    api.postMessage.addFriendFromInvite(friendId, {
      friendId: state.id,
    });

    // The missing reciprocal side was queued for restoration.
    return true;
  }),
});
